import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GlassCard } from "./GlassCard";
import { Trophy, Star, Zap, TrendingUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GamificationPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewAchievement, setShowNewAchievement] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/gamification/stats`);
      setStats(response.data);
      
      // Show new achievement notification
      if (response.data.new_achievements?.length > 0) {
        const achievementId = response.data.new_achievements[0];
        const achievement = response.data.all_achievements[achievementId];
        setShowNewAchievement(achievement);
        toast.success(`🏆 ¡Nuevo logro: ${achievement.name}!`);
      }
    } catch (error) {
      console.error("Error fetching gamification stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <GlassCard className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/10 rounded w-1/3" />
          <div className="h-4 bg-white/10 rounded w-full" />
        </div>
      </GlassCard>
    );
  }

  if (!stats) return null;

  const xpProgress = stats.xp_for_next_level > 0 
    ? (stats.xp_in_level / stats.xp_for_next_level) * 100 
    : 0;

  return (
    <>
      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-orbitron text-lg text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-neon-yellow" />
            Nivel {stats.level}
          </h3>
          <div className="flex items-center gap-2 text-neon-cyan">
            <Zap className="w-4 h-4" />
            <span className="font-rajdhani font-bold">{stats.xp} XP</span>
          </div>
        </div>

        {/* XP Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-slate-400 mb-1">
            <span>Progreso al nivel {stats.level + 1}</span>
            <span>{stats.xp_in_level} / {stats.xp_for_next_level} XP</span>
          </div>
          <div className="h-3 bg-black/30 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${xpProgress}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-neon-cyan to-neon-purple rounded-full"
            />
          </div>
        </div>

        {/* Achievements */}
        <div>
          <h4 className="text-sm text-slate-400 mb-3 flex items-center gap-2">
            <Star className="w-4 h-4" />
            Logros ({stats.achievements?.length || 0})
          </h4>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {Object.entries(stats.all_achievements || {}).map(([id, achievement]) => {
              const isEarned = stats.achievements?.includes(id);
              return (
                <motion.div
                  key={id}
                  whileHover={{ scale: 1.1 }}
                  className={`relative p-3 rounded-lg text-center transition-all ${
                    isEarned
                      ? "bg-neon-purple/20 border border-neon-purple/50"
                      : "bg-white/5 border border-white/10 opacity-40"
                  }`}
                  title={`${achievement.name}: ${achievement.description}`}
                >
                  <span className="text-2xl">{achievement.icon}</span>
                  {isEarned && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-neon-green rounded-full" />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </GlassCard>

      {/* New Achievement Modal */}
      <AnimatePresence>
        {showNewAchievement && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowNewAchievement(null)}
          >
            <motion.div
              initial={{ scale: 0.5, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.5, y: 50 }}
              className="bg-void-paper border border-neon-purple/50 rounded-2xl p-8 text-center max-w-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.2, 1] }}
                transition={{ duration: 0.5 }}
                className="text-6xl mb-4"
              >
                {showNewAchievement.icon}
              </motion.div>
              <h2 className="font-orbitron text-2xl text-neon-purple mb-2">
                ¡Nuevo Logro!
              </h2>
              <h3 className="font-rajdhani text-xl text-white mb-2">
                {showNewAchievement.name}
              </h3>
              <p className="text-slate-400 mb-4">
                {showNewAchievement.description}
              </p>
              <div className="flex items-center justify-center gap-2 text-neon-cyan">
                <Zap className="w-5 h-5" />
                <span className="font-bold">+{showNewAchievement.xp} XP</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
